import React, { useState, useEffect } from 'react';
import { 
  getExpensesApi, 
  deleteExpenseApi,
  getExpoenseByIdApi
} from '../../../Apis/Api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faSearch, faFileExport } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import toast from 'react-hot-toast';
import ExpenseEditModal from './ExpenseEditModal';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';

const categories = [
  'rent', 'utilities', 'supplies', 'salaries', 'maintenance', 
  'chiya', 'food', 'transportation', 'marketing', 'office_supplies',
  'repairs', 'taxes', 'insurance', 'professional_services', 'other'
];

const paymentMethods = ['cash', 'bank', 'digital', 'need_to_give'];

const ViewExpenses = () => {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState({
    category: '',
    paymentMethod: '',
    startDate: '',
    endDate: ''
  });
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [currentExpense, setCurrentExpense] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    fetchExpenses();
  }, [filter, refreshKey]);

  const fetchExpenses = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filter.category) params.category = filter.category;
      if (filter.paymentMethod) params.paymentMethod = filter.paymentMethod;
      if (filter.startDate) params.startDate = filter.startDate;
      if (filter.endDate) params.endDate = filter.endDate;
      if (searchTerm) params.search = searchTerm;

      const response = await getExpensesApi(params);
      setExpenses(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch expenses');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteExpenseApi(id);
      toast.success('Expense deleted successfully');
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      toast.error('Failed to delete expense');
    } finally {
      setShowDeleteConfirm(null);
    }
  };

  const confirmDelete = (id) => {
    setShowDeleteConfirm(id);
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(null);
  };

  const handleEdit = async (id) => {
    try {
      const response = await getExpoenseByIdApi(id);
      setCurrentExpense(response.data);
      setEditModalOpen(true);
    } catch (error) {
      toast.error('Failed to fetch expense details');
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilter({
      ...filter,
      [name]: value
    });
  };

  const resetFilters = () => {
    setFilter({
      category: '',
      paymentMethod: '',
      startDate: '',
      endDate: ''
    });
    setSearchTerm('');
  };

  const handleExport = () => {
    if (expenses.length === 0) {
      toast.error('No expenses to export');
      return;
    }

    setExporting(true);

    try {
      const data = expenses.map(expense => ({
        'Date': moment(expense.date).format('YYYY-MM-DD'),
        'Description': expense.description,
        'Category': expense.category,
        'Payment Method': expense.paymentMethod,
        'Amount (NPR)': expense.amount,
        'Recorded At': moment(expense.createdAt).format('YYYY-MM-DD HH:mm')
      }));

      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Expenses');
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

      saveAs(blob, `Expenses_${moment().format('YYYYMMDD_HHmmss')}.xlsx`);
      toast.success('Export completed successfully!');
    } catch (error) {
      toast.error('Failed to export expenses');
    } finally {
      setExporting(false);
    }
  };

  const filteredExpenses = expenses.filter(expense => {
    if (searchTerm) {
      return (
        expense.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        expense.category.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return true;
  });

  const totalAmount = filteredExpenses.reduce((sum, expense) => sum + expense.amount, 0);

  const formatCategoryName = (value) => {
    return value
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  const handleUpdateComplete = async () => {
    try {
      await fetchExpenses(); // Explicitly call the GET API
      setEditModalOpen(false);
      toast.success('Expense updated and data refreshed!');
    } catch (error) {
      toast.error('Expense updated but failed to refresh data');
      setEditModalOpen(false);
    }
  };
  return (
    <div className="mt-5">
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
          <h2 className="text-gray-600 font-bold text-2xl mb-4 md:mb-0">Expense Records</h2>
          
          <div className="flex items-center gap-4">
            <div className="relative w-full md:w-64">
              <input
                type="text"
                placeholder="Search expenses..."
                className="pl-10 pr-4 py-2 w-full border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <FontAwesomeIcon 
                icon={faSearch} 
                className="absolute left-3 top-3 text-gray-400"
              />
            </div>
            
            <button
              onClick={handleExport}
              disabled={expenses.length === 0 || exporting}
              className={`px-4 py-2 rounded-md flex items-center gap-2 ${
                expenses.length === 0 || exporting 
                  ? 'bg-gray-400 text-gray-600 cursor-not-allowed' 
                  : 'bg-green-600 text-white hover:bg-green-700'
              }`}
            >
              <FontAwesomeIcon icon={faFileExport} />
              {exporting ? 'Exporting...' : 'Export'}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              name="category"
              value={filter.category}
              onChange={handleFilterChange}
              className="w-full p-2 border rounded-md"
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>
                  {formatCategoryName(cat)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
            <select
              name="paymentMethod"
              value={filter.paymentMethod}
              onChange={handleFilterChange}
              className="w-full p-2 border rounded-md"
            >
              <option value="">All Methods</option>
              {paymentMethods.map(method => (
                <option key={method} value={method}>
                  {formatCategoryName(method)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
            <input
              type="date"
              name="startDate"
              value={filter.startDate}
              onChange={handleFilterChange}
              className="w-full p-2 border rounded-md"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
            <input
              type="date"
              name="endDate"
              value={filter.endDate}
              onChange={handleFilterChange}
              className="w-full p-2 border rounded-md"
            />
          </div>
        </div>

        <button
          onClick={resetFilters}
          className="mb-4 text-sm text-indigo-600 hover:text-indigo-800"
        >
          Reset Filters
        </button>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white">
                <thead>
                  <tr className="bg-gray-100 text-gray-600 uppercase text-sm leading-normal">
                    <th className="py-3 px-6 text-left">Date</th>
                    <th className="py-3 px-6 text-left">Description</th>
                    <th className="py-3 px-6 text-left">Category</th>
                    <th className="py-3 px-6 text-left">Payment Method</th>
                    <th className="py-3 px-6 text-right">Amount (NPR)</th>
                    <th className="py-3 px-6 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="text-gray-600 text-sm">
                  {filteredExpenses.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center py-4">No expenses found</td>
                    </tr>
                  ) : (
                    filteredExpenses.map((expense) => (
                      <tr key={expense._id} className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="py-3 px-6 text-left whitespace-nowrap">
                          {moment(expense.date).format('MMM D, YYYY')}
                        </td>
                        <td className="py-3 px-6 text-left">{expense.description}</td>
                        <td className="py-3 px-6 text-left capitalize">
                          {formatCategoryName(expense.category)}
                        </td>
                        <td className="py-3 px-6 text-left">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            expense.paymentMethod === 'cash' ? 'bg-blue-100 text-blue-800' :
                            expense.paymentMethod === 'bank' ? 'bg-green-100 text-green-800' :
                            expense.paymentMethod === 'digital' ? 'bg-purple-100 text-purple-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {formatCategoryName(expense.paymentMethod)}
                          </span>
                        </td>
                        <td className="py-3 px-6 text-right font-medium">
                          {expense.amount.toLocaleString()}
                        </td>
                        <td className="py-3 px-6 text-center">
                          <div className="flex justify-center space-x-2">
                            <button
                              onClick={() => handleEdit(expense._id)}
                              className="text-indigo-600 hover:text-indigo-900"
                              title="Edit"
                            >
                              <FontAwesomeIcon icon={faEdit} />
                            </button>
                            {showDeleteConfirm === expense._id ? (
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => handleDelete(expense._id)}
                                  className="text-red-600 hover:text-red-900 text-xs font-medium"
                                  title="Confirm Delete"
                                >
                                  Confirm
                                </button>
                                <button
                                  onClick={cancelDelete}
                                  className="text-gray-600 hover:text-gray-900 text-xs font-medium"
                                  title="Cancel"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => confirmDelete(expense._id)}
                                className="text-red-600 hover:text-red-900"
                                title="Delete"
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-50 font-bold">
                    <td colSpan="4" className="py-3 px-6 text-right">Total:</td>
                    <td className="py-3 px-6 text-right">{totalAmount.toLocaleString()}</td>
                    <td className="py-3 px-6"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        )}
      </div>

      {editModalOpen && (
        <ExpenseEditModal
          expense={currentExpense}
          onClose={() => setEditModalOpen(false)}
          onUpdate={handleUpdateComplete}
        />
      )}
    </div>
  );
};

export default ViewExpenses;